import type {
  DraftState,
  WizardFile,
  WizardSampleGroup,
  DiscoverySummary,
  PlannedOrderSettings
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { fileKeyOf } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import { consensusSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { DetectionRequest, DetectionResult } from './detectionCore.ts'
import { filesToScan } from './detectionCore.ts'
import type { RegistryRow } from './discovery.ts'
import type { LabPrimerHistory } from './estimationCore.ts'
import { settledByMates, summarizeDiscovery } from './discovery.ts'
import type { OrgDefaults } from './orderSettingsPlan.ts'
import { keepLabSettings, planOrderSettings } from './orderSettingsPlan.ts'
import { planItems, REJECTION_TEXT } from './itemPlan.ts'
import { hasCorrectFileEnding } from '../../Util/SampleFileUtils.ts'
import { toWizardFile } from './wizardFiles.ts'
import { getSequencingPlatformLabel } from '../../Util/sequencingPlatform.ts'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import { pairDroppedWithWaiting } from './reverseFiles.ts'

export type DroppedFile = { key: string; file: File }

export type SortedDrop = {
  /** Files the draft does not know yet, or rejected before and now judged afresh. */
  added: WizardFile[]
  /** Known files that lost their handle or failed, and can upload again. */
  reattached: string[]
  /** Every dropped file with its key, for attaching handles. */
  handles: DroppedFile[]
  /** The added FASTQ files, which detection and planning look at. */
  toAnalyse: DroppedFile[]
  /** Sample sheets, read again even when dropped before. */
  sheets: DroppedFile[]
  /** Dropped files the draft already holds, with nothing to do for them. */
  known: number
  /** Further copies of a file within this drop. */
  repeated: number
  /** Added files refused because the order holds a different file under the same name. */
  conflicts: string[]
}

export const SAME_NAME_REJECTION = 'A different file with the same name is already in this order'

/** A file whose reading failed: rejected, so dropping it again reads it afresh. */
export const DETECTION_FAILED_REJECTION = 'Reading the file failed — drop it again'

const identityKey = (file: { name: string; size: number }) => `${file.size}:${file.name}`

/**
 * Splits a drop into new and known files by name and size, never by content. The last-modified time is
 * ignored: a copy of the same file carries another one, and the pool refuses a name it holds anyway.
 */
export const sortDrop = (draft: DraftState, files: readonly File[]): SortedDrop => {
  const keyByIdentity = new Map<string, string>()
  const serverKeyByName = new Map<string, string>()
  const heldNames = new Map<string, number | undefined>()
  for (const file of Object.values(draft.files)) {
    if (file.fromServer) {
      serverKeyByName.set(file.name, file.key)
      continue
    }
    const identity = identityKey(file)
    const current = keyByIdentity.get(identity)
    if (!current || draft.files[current].rejection) {
      keyByIdentity.set(identity, file.key)
    }
    if (file.kind === 'fastq' && !file.rejection) {
      heldNames.set(file.name, file.size)
    }
  }
  /* a removed item's file stays in the pool under its name */
  for (const released of draft.releasedFiles ?? []) {
    if (!heldNames.has(released.name)) {
      heldNames.set(released.name, released.size)
    }
  }
  const seen = new Set<string>()
  const sorted: SortedDrop = {
    added: [],
    reattached: [],
    handles: [],
    toAnalyse: [],
    sheets: [],
    known: 0,
    repeated: 0,
    conflicts: []
  }
  for (const file of files) {
    const identity = identityKey(file)
    const key = keyByIdentity.get(identity) ?? serverKeyByName.get(file.name) ?? fileKeyOf(file)
    if (seen.has(key)) {
      sorted.repeated++
      continue
    }
    seen.add(key)
    keyByIdentity.set(identity, key)
    sorted.handles.push({ key, file })
    const known = draft.files[key]
    if (known?.kind === 'sheet') {
      sorted.sheets.push({ key, file })
      continue
    }
    if (known && !known.rejection) {
      if (known.upload?.status === 'needs-readd' || known.upload?.status === 'failed') {
        sorted.reattached.push(key)
      } else {
        sorted.known++
      }
      continue
    }
    const wizardFile: WizardFile = { ...toWizardFile(file), key }
    const heldSize = heldNames.get(file.name)
    if (wizardFile.kind === 'fastq' && heldNames.has(file.name) && heldSize !== undefined && heldSize !== file.size) {
      sorted.added.push({ ...wizardFile, rejection: SAME_NAME_REJECTION })
      sorted.conflicts.push(key)
      continue
    }
    sorted.added.push(wizardFile)
    if (wizardFile.kind === 'fastq') {
      heldNames.set(file.name, file.size)
      sorted.toAnalyse.push({ key, file })
    } else if (wizardFile.kind === 'sheet') {
      sorted.sheets.push({ key, file })
    }
  }
  return sorted
}

const MATE_REJECTIONS = new Set([
  REJECTION_TEXT['no-mate'],
  REJECTION_TEXT['mate-rejected'],
  REJECTION_TEXT['awaiting-layout'],
  REJECTION_TEXT['awaiting-mate']
])

/**
 * FASTQ files the draft turned away for want of a mate, whose handles are still at hand: a drop of new
 * files may bring the mates, so they are analysed with it rather than staying rejected.
 */
export const awaitingMates = (draft: DraftState, drop: SortedDrop, handleOf: (key: string) => File | undefined): DroppedFile[] => {
  if (drop.toAnalyse.length === 0) {
    return []
  }
  const dropped = new Set(drop.handles.map(({ key }) => key))
  return Object.values(draft.files).flatMap((file) => {
    const handle =
      file.kind === 'fastq' && file.rejection && MATE_REJECTIONS.has(file.rejection) && !dropped.has(file.key) && handleOf(file.key)
    return handle ? [{ key: file.key, file: handle }] : []
  })
}

export type DropAnalysis = {
  discovery?: DiscoverySummary
  settings?: PlannedOrderSettings
  platforms: Record<string, DetectedSequencingPlatform>
  rejections: Record<string, string>
  groups: WizardSampleGroup[]
  /** Dropped files that are the R2 of samples missing theirs: they join those samples. */
  attachments?: { groupKey: string; reverse: string }[]
}

export type DropDeps = {
  detectPlatforms: (files: File[]) => Promise<Map<File, DetectedSequencingPlatform>>
  detect: (request: DetectionRequest) => Promise<DetectionResult>
  /** `null` when the registry cannot be loaded for this account. */
  loadRegistry: () => Promise<RegistryRow[] | null>
  /** The organisation's earlier primer entries; without it a found pair's names keep registry order. */
  loadHistory?: () => Promise<LabPrimerHistory>
}

/**
 * Platform per file, then — for the draft's first FASTQ files — the read heads, primer registry scan and
 * sample-format classifier, then which files become which items. The order's settings come out of the
 * first drop that yields an item; later drops are planned against the settings the order already has.
 */
export const analyseDrop = async (
  draft: DraftState,
  toAnalyse: readonly DroppedFile[],
  org: OrgDefaults,
  deps: DropDeps
): Promise<DropAnalysis> => {
  if (toAnalyse.length === 0) {
    return { platforms: {}, rejections: {}, groups: [] }
  }
  const detected = await deps.detectPlatforms(toAnalyse.map(({ file }) => file))
  const platformOf = (file: File): DetectedSequencingPlatform => detected.get(file) ?? 'UNKNOWN'
  const platforms = Object.fromEntries(toAnalyse.map(({ key, file }) => [key, platformOf(file)]))
  /* files the upload refuses anyway must not sway the layout or format verdict */
  const uploadable = toAnalyse.filter(({ file }) => hasCorrectFileEnding(file.name))
  if (uploadable.length === 0) {
    return { platforms, rejections: Object.fromEntries(toAnalyse.map(({ key }) => [key, REJECTION_TEXT['wrong-extension']])), groups: [] }
  }
  const classify = !draft.discovery || (!draft.orderId && Object.keys(draft.groups).length === 0)
  const [registryRows, history] = classify ? await Promise.all([deps.loadRegistry(), deps.loadHistory?.() ?? null]) : [null, null]
  const scanned = new Set(classify ? filesToScan(uploadable.map(({ key, file }) => ({ key, name: file.name }))).map(({ key }) => key) : [])

  const result = await deps.detect({
    files: uploadable.map(({ key, file }) => ({
      key,
      name: file.name,
      size: file.size,
      platform: platformOf(file),
      blob: scanned.has(key) ? file : undefined
    })),
    registryRows,
    classify
  })

  const summary =
    classify && result.guess
      ? summarizeDiscovery(result.guess, {
          platform: consensusSequencingPlatform(uploadable.map(({ file }) => platformOf(file))),
          registryRows,
          history,
          layoutReason: result.layout.reason,
          layoutConfidence: result.layout.confidence,
          layoutPairs: result.layout.pairs.length,
          layoutUnmated: result.layout.unmated,
          filesScanned: result.filesScanned,
          runs: result.runs,
          readLengths: result.readLengths
        })
      : undefined
  const discovery = summary && { ...summary, primers: settledByMates(draft.discovery?.primers, summary.primers) }
  const planned = discovery && planOrderSettings(discovery, org)
  const settings = planned && draft.settings ? keepLabSettings(planned, draft.settings, discovery) : (planned ?? draft.settings)
  if (!settings) {
    throw new Error('No order settings to plan the files against')
  }

  /* the order's platform is settled by its first files: another platform's files belong in another order */
  const orderPlatform = classify ? undefined : draft.discovery?.platform
  const foreign = new Set(
    orderPlatform && orderPlatform !== 'UNKNOWN'
      ? toAnalyse.filter(({ file }) => ![orderPlatform, 'UNKNOWN'].includes(platformOf(file))).map(({ key }) => key)
      : []
  )
  const foreignText = (key: string) =>
    `Sequenced on ${getSequencingPlatformLabel(platforms[key] as SequencingPlatformKey) ?? platforms[key]}, this order on ${
      getSequencingPlatformLabel(orderPlatform as SequencingPlatformKey) ?? orderPlatform
    } — start another order for it`
  const unmated = settings.sampleConfiguration.source === 'undetermined' && !!(discovery ?? draft.discovery)?.layout.unmated
  /* until the lab says the mates are coming, a name pair the layout did not accept is not one */
  const pairsHeld = unmated && !draft.layoutAnswer
  const attachments = draft.orderId
    ? pairDroppedWithWaiting(
        { ...draft, settings },
        uploadable.filter(({ key }) => !foreign.has(key)).map(({ key, file }) => ({ key, name: file.name }))
      )
    : []
  const attached = new Set(attachments.map(({ reverse }) => reverse))
  const plan = planItems({
    files: toAnalyse
      .filter(({ key }) => !foreign.has(key) && !attached.has(key))
      .map(({ key, file }) => ({ key, name: file.name, platform: platformOf(file) })),
    pairs: pairsHeld
      ? []
      : result.layout.pairs
          .filter((_, i) => result.layout.pairChecks[i] !== 'contradicted')
          .map(([a, b]): [string, string] => [uploadable[a].key, uploadable[b].key])
          .filter(([a, b]) => !foreign.has(a) && !foreign.has(b) && !attached.has(a) && !attached.has(b)),
    sampleConfiguration: settings.sampleConfiguration.value
  })
  for (const key of foreign) {
    plan.rejections[key] = foreignText(key)
  }

  const waiting = REJECTION_TEXT[draft.layoutAnswer ? 'awaiting-mate' : 'awaiting-layout']
  const rejections = unmated
    ? Object.fromEntries(
        Object.entries(plan.rejections).map(([key, reason]) => [key, reason === REJECTION_TEXT['no-mate'] ? waiting : reason])
      )
    : plan.rejections

  return {
    discovery,
    settings: discovery ? settings : undefined,
    platforms,
    rejections,
    groups: plan.groups,
    ...(attachments.length > 0 ? { attachments } : {})
  }
}
